import Typography from '@mui/material/Typography';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import { lighten } from '@mui/material/styles';
import { Box } from '@mui/system';
import Button from '@mui/material/Button';
import { selectItemById, deleteFileItem } from 'app/store/fileItemsSlice';
import ItemIcon from './ItemIcon';
import UserAvatar from '../user/UserAvatar';
import UserName from '../user/UserName';
import format from 'date-fns/format';
import { useNavigate } from 'react-router-dom';
import { confirmationDialog, bytesToKB, dispatchResultMessage } from 'app/shared-components/utils.js';
import NavLinkAdapter from '@fuse/core/NavLinkAdapter';
import AIChatButton from 'app/shared-components/aichat/AIChatButton';
import EditFileDialog from 'app/shared-components/file/EditFileDialog';


function FileItemDetail(props) {
  const  { itemId, returnPath, isModal = false, item: propItem } = props;
  const navigate= useNavigate();
  const dispatch = useDispatch();
  const [edit, setEdit]= useState(false);
  const storeItem = useSelector((state) => selectItemById(state, itemId));
  const item = storeItem || propItem;
  if (!item) return null;
  const isFolder= item.type === 'folder';
  const sizeLabel = isFolder ? 'Files' : 'Size';
  const sizeValue = isFolder ? item.size : bytesToKB(item.size);

  
  function handleDeleteItem() {
	confirmationDialog(dispatch, "Permanently Delete?", 
	                   "Are you sure you want to delete this " + item.type + "? This cannot be undone.",
	 (confirmed) => {
		  if (confirmed){
			 dispatch(deleteFileItem({fileId: item.id, folderId: item.parentId})).then((resp) => {
		        dispatchResultMessage(dispatch, resp, "Item Deleted");
		        navigate(returnPath); 
		     });
	      }
	  }
	);
  }
  
  function handleEditItem() {
     setEdit(true);
  }

  const content = (
    <div className="file-details p-24 sm:p-32">
      {!isModal && (
        <IconButton
          className="absolute z-20 top-0 right-0 m-6 w-32 h-32 min-h-32"
          onClick={() => navigate(returnPath)}
        >
          <FuseSvgIcon size={20}>heroicons-solid:x</FuseSvgIcon>
        </IconButton>
      )}
      
      <div className="flex items-center">
          <ItemIcon className="" type={item.type} />
          <Typography className="ml-20 text-18 font-medium">{item.name}</Typography>
      </div>
      

      <div className="text-16 font-medium mt-32">Information</div>
      <div className="flex flex-col mt-16 border-t border-b divide-y font-medium">
        <div className="flex items-center justify-between py-12">
          <Typography color="text.secondary">Created By</Typography>
          <div className="flex items-center">
              <UserAvatar user={item.createdBy}/>
	          <UserName className="ml-12" user={item.createdBy}/>
	      </div>
        </div>
        <div className="flex items-center justify-between py-12">
          <Typography color="text.secondary">Created At</Typography>
          <Typography>{format(new Date(item.createdAt), 'EEEE,MMMM do, yyyy hh:mm a')}</Typography>
        </div>
        <div className="flex items-center justify-between py-12">
          <Typography color="text.secondary">Modified At</Typography>
          <Typography>{item.lastModified? format(new Date(item.lastModified), 'EEEE,MMMM do, yyyy hh:mm a') : null}</Typography>
        </div>
        <div className="flex items-center justify-between py-12">
          <Typography color="text.secondary">{sizeLabel}</Typography>
          <Typography>{sizeValue}</Typography>
        </div>
        <div className="flex items-center justify-between py-12">
            <Typography color="text.secondary">Included in AI Knowledge Base?</Typography>
            <Typography>{item.knowledgeBase ? 'Yes' : 'No'}</Typography>
        </div>
      </div>

      {item.description && (
        <>
          <div className="text-16 font-medium mt-32 pb-16 border-b">Description</div>
          <Typography className="py-12">{item.description}</Typography>
        </>
      )}

      <div className="flex flex-auto justify-end items-center w-full mt-32">
        {!isFolder &&
        <>
        <AIChatButton fileId={itemId} />
        {item.objectUrl && (
          <Button component={NavLinkAdapter} to={item.objectUrl} target="_blank" 
                  className="mx-8 whitespace-nowrap" download={item.name}
            variant="contained"
            color="secondary" >
            Download
          </Button>
        )}
        {storeItem && <Button className="mx-8 whitespace-nowrap" color="secondary" variant="contained" onClick={handleEditItem}>Edit</Button>}
        </>
        }
        {storeItem && <Button className="mx-8 whitespace-nowrap" color="secondary" variant="contained" onClick={handleDeleteItem}>Delete</Button>}
      </div>
    </div>
  );

  return (
    <>
      {isModal ? (
        content
      ) : (
        <motion.div
          initial={{ y: 50, opacity: 0.8 }}
          animate={{ y: 0, opacity: 1, transition: { delay: 0.3 } }}
        >
          {content}
        </motion.div>
      )}
      {edit && (
        <EditFileDialog itemId={itemId} onCloseDialog={() => setEdit(false)} />
      )}
    </>
  );
}

export default FileItemDetail;
